import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './LeavePermissionPage.css';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const LeavePermissionPage = () => {
  const [formData, setFormData] = useState({
    staffId: '',
    startDate: '',
    endDate: '',
    reason: '',
    leaveType: 'casual'
  });
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [activeStaff, setActiveStaff] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState('staff');
  const [currentUserId, setCurrentUserId] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);


  const { currentUser } = useAuth();
  const API_URL = `${API_CONFIG.BASE_URL}/api`;

  const fetchUserInfo = useCallback(async () => {
    try {
      if (!currentUser) return;
      
      const idToken = await currentUser.getIdToken(true);
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      setUserRole(response.data.role || 'staff');
      setCurrentUserId(response.data._id);
      setCurrentUserName(response.data.name);
      
      // Check if user is Super Admin
      const isSuperAdminUser = response.data.role === 'superadmin' || response.data.role === 'super_admin';
      setIsSuperAdmin(isSuperAdminUser);
      
      // If user is not admin, pre-select their own ID in the form
      if (response.data.role !== 'admin') {
        setFormData(prev => ({ ...prev, staffId: response.data._id }));
      }
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  }, [currentUser, API_URL]);

  const fetchActiveStaff = useCallback(async () => {
    try {
      if (!currentUser) return [];
      
      const idToken = await currentUser.getIdToken(true);
      const response = await axios.get(`${API_URL}/auth/users/all`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      return [];
    }
  }, [currentUser, API_URL]);

  const fetchData = useCallback(async () => {
    try {
      if (!currentUser) return;
      
      setLoading(true);
      setError('');
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const [staffData, leaveData] = await Promise.all([
        fetchActiveStaff(),
        axios.get(`${API_URL}/leave-requests`, { headers })
      ]);

      setActiveStaff(Array.isArray(staffData) ? staffData : []);
      
      // Fix: Check if leaveData.data exists and is an array
      let leaveRequestsData = [];
      
      if (leaveData.data) {
        if (Array.isArray(leaveData.data)) {
          leaveRequestsData = leaveData.data;
        } else if (leaveData.data.data && Array.isArray(leaveData.data.data)) {
          // Handle nested data structure
          leaveRequestsData = leaveData.data.data;
        } else if (typeof leaveData.data === 'object') {
          // Handle object with data property
          leaveRequestsData = leaveData.data.data || [];
        }
      }
      
      setLeaveRequests(leaveRequestsData);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [currentUser, fetchActiveStaff, API_URL]);

  useEffect(() => {
    if (currentUser) {
      fetchUserInfo();
      fetchData();
    }
  }, [currentUser, fetchUserInfo, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    try {
      if (!currentUser) return;
      if (!formData.staffId) {
        throw new Error('Please select a staff member');
      }

      const idToken = await currentUser.getIdToken(true);
      const response = await axios.post(
        `${API_URL}/leave-requests`,
        formData,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      
      setLeaveRequests(prev => [...prev, response.data]);
      setFormData({
        staffId: userRole === 'admin' ? '' : currentUserId,
        startDate: '',
        endDate: '',
        reason: '',
        leaveType: 'casual'
      });
    } catch (err) {
      console.error('Error creating leave request:', err);
      setError(err.message || err.response?.data?.error || 'Failed to create leave request');
    }
  };

  const handleUpdate = async (id) => {
    try {
      if (!currentUser) return;
      
      const idToken = await currentUser.getIdToken(true);
      const response = await axios.put(
        `${API_URL}/leave-requests/${id}`,
        formData,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      
      setLeaveRequests(prev =>
        prev.map(item => ((item._id || item.id) === id ? response.data : item))
      );
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update leave request');
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!currentUser) return;
      
      const idToken = await currentUser.getIdToken(true);
      await axios.delete(`${API_URL}/leave-requests/${id}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      setLeaveRequests(prev => prev.filter(item => (item._id || item.id) !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete leave request');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id || item.id);
    setFormData({ ...item });
  };

  const resetForm = () => {
    setFormData({
      staffId: userRole === 'admin' ? '' : currentUserId,
      startDate: '',
      endDate: '',
      reason: '',
      leaveType: 'casual'
    });
    setEditingId(null);
  };

  const handleLeaveAction = async (id, status) => {
    try {
      if (!currentUser) return;
      
      const idToken = await currentUser.getIdToken(true);
      const response = await axios.put(
        `${API_URL}/leave-requests/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' } }
      );
      setLeaveRequests(prev => prev.map(req => ((req._id || req.id) === id ? response.data : req)));
    } catch (err) {
      console.error('Error updating leave status:', err);
      setError(err.response?.data?.error || 'Failed to update leave status');
    }
  };

  const renderStaffSelect = () => (
    <select
      value={formData.staffId}
      onChange={(e) => handleInput('staffId', e.target.value)}
      disabled={loading || editingId || userRole !== 'admin'}
      required
    >
      <option value="">Select Staff</option>
      {userRole === 'admin' ? (
        Array.isArray(activeStaff) && activeStaff.map(staff => (
          <option key={staff._id || staff.id || `staff-${staff.name}-${staff.email}`} value={staff._id || staff.id}>
            {staff.name} ({staff.role || 'Staff'}) - {staff.department || 'No Department'}
          </option>
        ))
      ) : (
        <option key={currentUserId || 'current-user'} value={currentUserId}>
          {currentUserName} (Your Account)
        </option>
      )}
    </select>
  );

  const handlePrint = () => {
    // Create a comprehensive print view with proper heading
    const printContent = `
      <html>
        <head>
          <title>Leave Requests Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #007bff;
              padding-bottom: 20px;
            }
            .print-title {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 10px;
            }
            .print-subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 5px;
            }
            .print-date {
              font-size: 14px;
              color: #888;
            }
            .stats-section {
              margin: 20px 0;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 5px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-item {
              text-align: center;
              padding: 10px;
              background: white;
              border-radius: 5px;
              border: 1px solid #ddd;
            }
            .stat-number {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
            }
            .stat-label {
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0;
              font-size: 12px;
            }
            th, td { 
              border: 1px solid #333; 
              padding: 8px; 
              text-align: left;
            }
            th { 
              background-color: #007bff; 
              color: white;
              font-weight: bold; 
              text-align: center;
            }
            .badge-pending { 
              background-color: #ffc107; 
              color: #000; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
              font-weight: bold;
            }
            .badge-approved { 
              background-color: #28a745; 
              color: white; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
              font-weight: bold;
            }
            .badge-rejected { 
              background-color: #dc3545; 
              color: white; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
              font-weight: bold;
            }
            .print-footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Leave Request Management Report</div>
            <div class="print-subtitle">Staff Leave Management System</div>
            <div class="print-date">Generated: ${new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at ${new Date().toLocaleTimeString()}</div>
          </div>

          <div class="stats-section">
            <h3 style="margin-top: 0; color: #007bff;">Leave Request Summary</h3>
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">${leaveRequests.length}</div>
                <div class="stat-label">Total Requests</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #ffc107;">${leaveRequests.filter(req => req.status === 'pending').length}</div>
                <div class="stat-label">Pending</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #28a745;">${leaveRequests.filter(req => req.status === 'approved').length}</div>
                <div class="stat-label">Approved</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #dc3545;">${leaveRequests.filter(req => req.status === 'rejected').length}</div>
                <div class="stat-label">Rejected</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Leave Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${leaveRequests.length > 0 ? 
                leaveRequests.map((req) => `
                  <tr>
                    <td><strong>${req.staffId?.name || 'Unknown'}</strong></td>
                    <td>${req.leaveType}</td>
                    <td>${new Date(req.startDate).toLocaleDateString()}</td>
                    <td>${new Date(req.endDate).toLocaleDateString()}</td>
                    <td>${req.reason}</td>
                    <td>
                      <span class="badge-${req.status}">${req.status}</span>
                    </td>
                  </tr>
                `).join('') : 
                `<tr><td colspan="6" style="text-align: center; padding: 20px; color: #666;">No leave requests available</td></tr>`
              }
            </tbody>
          </table>

          <div class="print-footer">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total Requests:</strong> ${leaveRequests.length} | <strong>Pending:</strong> ${leaveRequests.filter(req => req.status === 'pending').length} | <strong>Approved:</strong> ${leaveRequests.filter(req => req.status === 'approved').length} | <strong>Rejected:</strong> ${leaveRequests.filter(req => req.status === 'rejected').length}</p>
            <p style="margin-top: 10px; font-size: 10px; color: #999;">
              This report shows all leave requests from the Leave Request Management System.
            </p>
          </div>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownload = () => {
    const table = document.querySelector('.data-table');
    if (!table) {
      alert('No table found to download');
      return;
    }

    const tableClone = table.cloneNode(true);
    
    const interactiveSelectors = [
      'button', 'input', 'select', 'textarea',
      '[onclick]', '[onchange]', '[oninput]', '[onmousedown]', '[onmouseup]',
      '.btn', '.button', '.form-control', '.form-select', '.input-group',
      '.actions', '.header-actions', '.controls', '.interactive',
      '.clickable', '.dropdown', '.nav', '.navbar'
    ];
    
    interactiveSelectors.forEach(selector => {
      const elements = tableClone.querySelectorAll(selector);
      elements.forEach(element => element.remove());
    });
    
    // Remove any elements with interactive classes or attributes
    const allElements = tableClone.querySelectorAll('*');
    allElements.forEach(element => {
      // Remove elements with interactive classes
      const classList = element.className;
      if (typeof classList === 'string') {
        const interactiveClasses = ['btn', 'button', 'input', 'select', 'form', 'interactive', 'clickable', 'actions'];
        const hasInteractiveClass = interactiveClasses.some(cls => classList.includes(cls));
        if (hasInteractiveClass) {
          element.remove();
          return;
        }
      }
      
      // Remove elements with event handlers
      const hasEventHandlers = element.onclick || element.onchange || element.oninput || 
                              element.onmousedown || element.onmouseup || element.onkeydown;
      if (hasEventHandlers) {
        element.remove();
        return;
      }
      
      // Remove elements with interactive attributes
      const interactiveAttrs = ['onclick', 'onchange', 'oninput', 'onmousedown', 'onmouseup', 'onkeydown'];
      const hasInteractiveAttr = interactiveAttrs.some(attr => element.hasAttribute(attr));
      if (hasInteractiveAttr) {
        element.remove();
      }
    });
    
    // Remove action columns and headers
    const actionHeaders = tableClone.querySelectorAll('th.actions, td.actions, .action-column, th:last-child');
    actionHeaders.forEach(element => element.remove());
    
    // Remove the last column (Actions column) from all rows
    const allRows = tableClone.querySelectorAll('tr');
    allRows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      if (cells.length > 0) {
        // Remove the last cell (Actions column)
        const lastCell = cells[cells.length - 1];
        if (lastCell.textContent.toLowerCase().includes('action') || 
            lastCell.classList.contains('actions')) {
          lastCell.remove();
        }
      }
    });

    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Leave Requests Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px; 
              font-size: 18px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
              font-size: 12px;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              text-align: center;
            }
            .badge-pending { 
              background-color: #ffc107; 
              color: #000; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
            }
            .badge-approved { 
              background-color: #28a745; 
              color: white; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
            }
            .badge-rejected { 
              background-color: #dc3545; 
              color: white; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
            }
            button,
            input,
            select,
            textarea,
            .actions,
            .header-actions,
            .controls,
            .interactive,
            .clickable,
            .btn,
            .button,
            .form-control,
            .form-select,
            .input-group,
            .dropdown,
            .nav,
            .navbar,
            .no-print,
            [onclick],
            [onchange],
            [oninput],
            [onmousedown],
            [onmouseup],
            [onkeydown] { 
              display: none !important; 
              visibility: hidden !important;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Leave Requests Report</h1>
          ${tableClone.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div className="leave-permission-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Leave Request Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={fetchData}
            disabled={loading}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
          <button 
            onClick={handlePrint}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🖨️ Print
          </button>
          <button 
            onClick={handleDownload}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📄 PDF
          </button>
        </div>
      </div>
      {error && <p className="error-message">{error}</p>}

      {loading ? (
        <div className="loading-indicator">Loading data...</div>
      ) : (
        <div className="content-wrapper">
          {/* Hide form for Super Admin - only HR Admin can add leave requests */}
          {!isSuperAdmin && (
            <div className="crud-form">
              {renderStaffSelect()}

              <select
                value={formData.leaveType}
                onChange={(e) => handleInput('leaveType', e.target.value)}
                disabled={loading}
              >
                <option value="casual">Casual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="annual">Annual Leave</option>
                <option value="maternity">Maternity Leave</option>
                <option value="paternity">Paternity Leave</option>
                <option value="unpaid">Unpaid Leave</option>
                <option value="other">Other</option>
              </select>

              <input
                type="date"
                placeholder="Start Date"
                value={formData.startDate}
                onChange={(e) => handleInput('startDate', e.target.value)}
                disabled={loading}
                required
              />

              <input
                type="date"
                placeholder="End Date"
                value={formData.endDate}
                onChange={(e) => handleInput('endDate', e.target.value)}
                disabled={loading}
                required
              />

              <input
                type="text"
                placeholder="Reason"
                value={formData.reason}
                onChange={(e) => handleInput('reason', e.target.value)}
                disabled={loading}
                required
              />

              {editingId ? (
                <>
                  <button 
                    className="save-btn" 
                    onClick={() => handleUpdate(editingId)}
                    disabled={loading}
                  >
                    <FaSave /> Save
                  </button>
                  <button 
                    className="cancel-btn" 
                    onClick={resetForm}
                    disabled={loading}
                  >
                    <FaTimes /> Cancel
                  </button>
                </>
              ) : (
                <button 
                  className="add-btn" 
                  onClick={handleCreate}
                  disabled={loading || !formData.staffId}
                >
                  <FaPlus /> Add
                </button>
              )}
            </div>
          )}

          {/* Show info message for Super Admin */}
          {isSuperAdmin && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #2196f3', 
              borderRadius: '4px', 
              marginBottom: '20px',
              color: '#1976d2'
            }}>
              <strong>Super Admin View:</strong> You can only approve or reject leave requests. HR Admin is responsible for adding new leave requests.
            </div>
          )}

          {/* Desktop Table View */}
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Leave Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
                         <tbody>
               {(() => {
                 return leaveRequests.length > 0 ? (
                   leaveRequests.map((req) => {
                     const requestId = req._id || req.id;
                     
                     return (
                     <tr key={requestId}>
                       <td>{req.staffId?.name || 'Unknown'}</td>
                       <td>{req.leaveType}</td>
                       <td>{new Date(req.startDate).toLocaleDateString()}</td>
                       <td>{new Date(req.endDate).toLocaleDateString()}</td>
                       <td>{req.reason}</td>
                       <td>
                         <span className={`badge-${req.status}`}>{req.status}</span>
                       </td>
                       <td>
                         {/* Show edit/delete only for HR Admin or the request owner */}
                         {(!isSuperAdmin && (userRole === 'admin' || req.staffId?._id === currentUserId)) && (
                           <>
                             <button 
                               className="edit-btn action-btn" 
                               onClick={() => handleEdit(req)}
                               disabled={loading}
                             >
                               <FaEdit />
                             </button>
                             <button 
                               className="delete-btn action-btn" 
                               onClick={() => handleDelete(requestId)}
                               disabled={loading}
                             >
                               <FaTrash />
                             </button>
                           </>
                         )}
                         {/* Show approve/reject for Super Admin and HR Admin */}
                         {(isSuperAdmin || userRole === 'admin') && req.status === 'pending' && (
                           <>
                             <button
                               className="action-btn approve"
                               onClick={() => handleLeaveAction(requestId, 'approved')}
                               disabled={loading}
                             >
                               Approve
                             </button>
                             <button
                               className="action-btn reject"
                               onClick={() => handleLeaveAction(requestId, 'rejected')}
                               disabled={loading}
                             >
                               Reject
                             </button>
                           </>
                         )}
                       </td>
                     </tr>
                   );
                 })
                                ) : (
                   <tr>
                     <td colSpan="7">No leave requests available.</td>
                   </tr>
                 );
               })()}
             </tbody>
          </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards-container">
            {leaveRequests.length > 0 ? (
              leaveRequests.map((req) => {
                const requestId = req._id || req.id;
                
                return (
                  <div key={requestId} className="leave-card">
                    <div className="card-header">
                      <div className="card-title">
                        <h4>{req.staffId?.name || 'Unknown'}</h4>
                        <span className={`card-status badge-${req.status}`}>
                          {req.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-content">
                      <div className="card-row">
                        <span className="card-label">Leave Type:</span>
                        <span className="card-value">{req.leaveType}</span>
                      </div>
                      
                      <div className="card-row">
                        <span className="card-label">Start Date:</span>
                        <span className="card-value">{new Date(req.startDate).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="card-row">
                        <span className="card-label">End Date:</span>
                        <span className="card-value">{new Date(req.endDate).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="card-row">
                        <span className="card-label">Reason:</span>
                        <span className="card-value">{req.reason}</span>
                      </div>
                    </div>
                    
                    <div className="card-actions">
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {/* Show edit/delete only for HR Admin or the request owner */}
                        {(!isSuperAdmin && (userRole === 'admin' || req.staffId?._id === currentUserId)) && (
                          <>
                            <button 
                              className="card-action-btn edit-btn" 
                              onClick={() => handleEdit(req)}
                              disabled={loading}
                            >
                              <FaEdit />
                              Edit
                            </button>
                            <button 
                              className="card-action-btn delete-btn" 
                              onClick={() => handleDelete(requestId)}
                              disabled={loading}
                            >
                              <FaTrash />
                              Delete
                            </button>
                          </>
                        )}
                        {/* Show approve/reject for Super Admin and HR Admin */}
                        {(isSuperAdmin || userRole === 'admin') && req.status === 'pending' && (
                          <>
                            <button
                              className="card-action-btn approve-btn"
                              onClick={() => handleLeaveAction(requestId, 'approved')}
                              disabled={loading}
                            >
                              ✓ Approve
                            </button>
                            <button
                              className="card-action-btn reject-btn"
                              onClick={() => handleLeaveAction(requestId, 'rejected')}
                              disabled={loading}
                            >
                              ✗ Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="mobile-empty-state">
                <div className="empty-icon">
                  📋
                </div>
                <h3>No Leave Requests</h3>
                <p>No leave requests available at this time.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeavePermissionPage;